// LengthStagePicker — Iter160i (Randy pt-length-picker-unification).
//
// Shared length stager for YTRE + YT Replies + Public Q&A. Reads the
// silo's answer-style presets from `/api/s/{slug}/answer-styles` — the
// same endpoint LengthBump (Public Q&A) uses. Ensures every response
// surface honors the owner's per-silo customization (adds / removes /
// renames of styles are picked up automatically).
//
// Contract:
//   - `slug` (required) — silo slug for the fetch
//   - `staged` — currently-staged preset id (string) or null
//   - `onStage(id)` — called when the user picks a preset from the popover.
//                    When `showClearOption` is on, `null` means "cleared".
//   - `disabled` — busy flag from parent
//   - `entryId` — for data-testid uniqueness across a canvas
//
// Iter160p (Randy pt-yt-length-migration, Feb 2026) — extended to
// support YTDraftCard's one-shot override semantics. Two new props:
//   - `showClearOption` — when true, prepends a "Use default" affordance
//     that clears staged to null (button falls back to unstagedLabel).
//     Perfect for YT Replies where "no override" means "use the
//     integration's saved answer_length_{questions|non_questions}".
//   - `unstagedLabel` — the pill label to show when staged is null AND
//     showClearOption is on. Defaults to "Length ▾".
//
// UX matches AskSiloNudgeStager: a pill that opens a popover, options
// just stage (no execute-on-click). Loading + error states surface
// gracefully inline so the parent doesn't need to handle them.

import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";

// Session-lifetime cache — one fetch per slug per tab-session. Prevents
// re-fetching for every card render.
const _cache = new Map();

async function fetchStylesFor(slug) {
  if (_cache.has(slug)) return _cache.get(slug);
  const p = api.get(`/s/${slug}/answer-styles`).then((r) => r.data);
  _cache.set(slug, p);
  return p;
}

// Iter160u (Randy pt-length-slow-load-fix, Feb 2026) — public API for
// surfaces that KNOW the user is going to open Length soon (e.g. YTRE
// on tab-load). Calling this on-mount warms the cache in the background
// so by the time the user clicks the pill, items are already there and
// the popover renders instantly instead of showing "Loading…" while the
// ingress round-trips a fresh fetch.
export function prefetchAnswerStyles(slug) {
  if (!slug) return;
  try { fetchStylesFor(slug); } catch { /* silent — warm-up is best-effort */ }
}

export default function LengthStagePicker({
  slug,
  staged,
  onStage,
  disabled,
  entryId,
  showClearOption = false,
  unstagedLabel = "Length",
  promptLabel = "Stage length for next Ask silo",
}) {
  const [items, setItems] = useState(null);
  const [defaultId, setDefaultId] = useState("free_choice");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const mounted = useRef(true);

  // Iter160u.b (Randy pt-length-still-loading, Feb 2026) — CRITICAL bug
  // fix. Previous code:  `useEffect(() => () => { mounted.current = false; }, []);`
  // only set mounted=false on UNMOUNT, never re-set it to TRUE on remount.
  // Under React StrictMode (enabled globally in this app), useEffect
  // simulates mount → unmount → remount on every dev render — so
  // mounted.current would go from true → false → and stay false
  // forever. When the fetch resolved, the .then handler saw mounted=false
  // and skipped setItems, leaving items=null → "Loading…" forever.
  // Fix: set mounted=true on EVERY mount (including the strict-remount)
  // and false only on the real cleanup. This is the canonical
  // "mounted ref" pattern.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!slug) return;
    fetchStylesFor(slug)
      .then((data) => {
        if (!mounted.current) return;
        setItems(data.items || []);
        setDefaultId(data.silo_default_preset || "free_choice");
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load length options."));
  }, [slug]);

  // When showClearOption is on and staged is null, the button reflects the
  // "no override" state (unstagedLabel, no color highlight). Otherwise the
  // staged id (or silo default) drives the label.
  const isCleared = showClearOption && (staged === null || staged === undefined);
  const effectiveId = staged || defaultId;
  const currentItem = useMemo(
    () => (items || []).find((it) => it.id === effectiveId),
    [items, effectiveId],
  );

  // Iter160s (Randy pt-ytre-length-label-fix, Feb 2026) — the button now
  // shows ONE canonical label. Randy's audit: "It's double dating itself
  // — it says length: length. It just needs to say length once." Root
  // cause: when `currentItem` couldn't resolve (silo has no "free_choice"
  // in answer-styles, or items still loading), the fallback used
  // `|| "Length"` which then rendered "Length: Length". Fix: only prefix
  // "Length:" when we actually have a valid resolved preset NAME to
  // render. Otherwise show the compact unstagedLabel ("Length") alone.
  //
  // Feb 2026 (Randy pt-ytre-length-honesty · iter169-Phase1) — extended.
  // Previously the pill said "Length: Let the AI decide" whenever the
  // silo default was `let_ai_decide` — even if the user had never touched
  // the pill. Randy: "By default it should just show the word Length.
  // Then after they click it, it can list the option they changed it
  // to." Fix: only render the resolved name when the user has EXPLICITLY
  // staged (truthy `staged` prop). The silo default still drives what
  // ACTUALLY gets used when Ask silo fires — it's just not advertised
  // in the label until the user has actively picked.
  const resolvedName = (!isCleared && staged) ? currentItem?.name : null;
  const label = resolvedName || unstagedLabel;
  const showPrefix = !!resolvedName;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`length-stage-picker-btn-${entryId || "default"}`}
          className={`text-[11px] px-2.5 py-1.5 rounded-md border flex items-center gap-1 ${
            showPrefix
              ? "border-[#B7CCE0] bg-[#EAF2FA] text-[#1B4E6E]"
              : "hairline text-[#4A5D4E] hover:bg-[#F0F1EE]"
          }`}
          title={items
            ? (showPrefix
                ? `Staged: "${label}". Click to change.`
                : "Pick a length — one-shot, applies to the next React/Re-draft.")
            : "Loading length options…"}
        >
          {showPrefix
            ? <>Length: <strong className="font-semibold">{label}</strong> {open ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />}</>
            : <>{label} {open ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />}</>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-72 p-1.5">
        <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#6B6A68] px-2 py-1.5">
          {promptLabel}
        </div>
        {err && (
          <div className="px-2 py-2 text-[10.5px] text-[#C16E5A]" data-testid={`length-stage-picker-error-${entryId || "default"}`}>
            {err}
          </div>
        )}
        {!items && !err && (
          <div className="px-2 py-2 text-[10.5px] text-[#6B6A68] italic">Loading…</div>
        )}
        {/* Iter160p — optional "Use default" clear-stage affordance. Only
            renders when the parent opts in. Sits at the top so it's the
            fastest way for a user to unstick a picked length. */}
        {showClearOption && items && (
          <button
            key="__clear__"
            onClick={() => { onStage(null); setOpen(false); }}
            data-testid={`length-stage-picker-opt-${entryId || "default"}-clear`}
            className={`w-full text-left px-2 py-1.5 rounded flex items-baseline gap-2 border-b border-[#F0F1EE] mb-1 pb-2 ${
              isCleared ? "bg-[#F0F1EE]" : "hover:bg-[#F0F1EE]"
            }`}
          >
            <span className="text-[12px] font-medium text-[#1C1B1A] flex-1">Use default</span>
            {isCleared && <span className="text-[10px] text-[#2F6B3A] font-medium">✓ staged</span>}
            <span className="text-[10px] text-[#6B6A68] ml-auto text-right">Uses your Settings-tab default</span>
          </button>
        )}
        {items && items.map((it) => {
          const isCurrent = !isCleared && effectiveId === it.id;
          const isSiloDefault = defaultId === it.id;
          return (
            <button
              key={it.id}
              onClick={() => { onStage(it.id); setOpen(false); }}
              data-testid={`length-stage-picker-opt-${entryId || "default"}-${it.id}`}
              className={`w-full text-left px-2 py-1.5 rounded flex items-baseline gap-2 ${isCurrent ? "bg-[#F0F1EE]" : "hover:bg-[#F0F1EE]"}`}
            >
              <span className="text-[12px] font-medium text-[#1C1B1A] flex-1">
                {it.name}
                {it.is_custom && <span className="text-[9px] text-[#6B6A68] uppercase tracking-wide ml-1.5">Custom</span>}
              </span>
              {isCurrent && <span className="text-[10px] text-[#2F6B3A] font-medium">✓ staged</span>}
              {!isCurrent && isSiloDefault && <span className="text-[9.5px] text-[#6B6A68] italic">creator default</span>}
              {it.description && (
                <span className="text-[10px] text-[#6B6A68] ml-auto text-right max-w-[10rem] truncate" title={it.description}>
                  {it.min_words > 0 ? `${it.min_words}-${it.max_words}w` : ""}
                </span>
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// Test-only helper — exposed so unit tests can flush between runs.
export function _clearLengthStagePickerCache() { _cache.clear(); }
